import { useState, useEffect, useCallback } from 'react';

export interface UseSearchDebounceOptions {
  emptyDelay?: number;
  valueDelay?: number;
  onQueryChange?: (query: string) => void;
}

export interface UseSearchDebounceReturn {
  searchValue: string;
  setSearchValue: (value: string) => void;
  debouncedQuery: string;
  clearSearch: () => void;
  handleSubmit: (e: React.FormEvent) => void;
}

/**
 * Debounced search input with separate delays for empty vs. non-empty values.
 * Local copy of AIM's `apps/aegix-aim-react/src/components/settings/hooks/useSearchDebounce.ts`
 * — kept in SMS rather than shared because the AIM source lives in app-internal
 * settings code; lifting to a `libs/react-hooks` lib is a separate, larger move
 * (see ADR draft for shared http-client / hooks consolidation).
 */
export function useSearchDebounce(
  options: UseSearchDebounceOptions = {},
): UseSearchDebounceReturn {
  const { emptyDelay = 1000, valueDelay = 500, onQueryChange } = options;
  const [searchValue, setSearchValue] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  useEffect(() => {
    const delay = searchValue === '' ? emptyDelay : valueDelay;
    const timer = setTimeout(() => {
      if (debouncedQuery !== searchValue) {
        setDebouncedQuery(searchValue);
        onQueryChange?.(searchValue);
      }
    }, delay);
    return () => clearTimeout(timer);
  }, [searchValue, debouncedQuery, emptyDelay, valueDelay, onQueryChange]);

  const clearSearch = useCallback(() => {
    setSearchValue('');
    setDebouncedQuery('');
    onQueryChange?.('');
  }, [onQueryChange]);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      setDebouncedQuery(searchValue);
      onQueryChange?.(searchValue);
    },
    [searchValue, onQueryChange],
  );

  return {
    searchValue,
    setSearchValue,
    debouncedQuery,
    clearSearch,
    handleSubmit,
  };
}
